import { Eye, EyeOff } from 'lucide-react';
import { type ReactElement, useId, useState } from 'react';
import { Input, type InputProps } from '../atoms/input';
import { Label } from '../atoms/label';
import { cn } from '../lib/utils';

export interface FormFieldProps extends Omit<InputProps, 'id' | 'hasError'> {
  readonly label: string;
  /** 검증 실패 메시지. 값이 있으면 오류 상태로 렌더링한다. */
  readonly error?: string | undefined;
  readonly description?: string | undefined;
  readonly isRequired?: boolean;
  readonly containerClassName?: string;
}

/**
 * 라벨 · 입력 · 설명 · 오류 메시지를 하나로 묶은 폼 필드.
 * 접근성을 위해 aria-describedby 로 설명/오류를 연결한다.
 * type="password" 면 오른쪽에 입력 내용 보기/숨기기 버튼을 단다 — 버튼 이름에 라벨 글자를 넣지 않아
 * 라벨로 입력을 찾는 쪽(getByLabelText)이 버튼까지 잡지 않는다.
 */
export const FormField = ({
  label,
  error,
  description,
  isRequired = false,
  containerClassName,
  className,
  type,
  ...inputProps
}: FormFieldProps): ReactElement => {
  const id = useId();
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;
  const isPassword = type === 'password';
  const [isRevealed, setIsRevealed] = useState(false);

  const describedBy =
    [error ? errorId : null, description ? descriptionId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      <Label htmlFor={id} isRequired={isRequired}>
        {label}
      </Label>

      <div className="relative">
        <Input
          id={id}
          type={isPassword && isRevealed ? 'text' : type}
          hasError={Boolean(error)}
          aria-describedby={describedBy}
          // 호출자의 px-0 같은 가로 패딩보다 뒤에 두어 버튼 자리를 확보한다.
          className={cn(className, isPassword && 'pr-10')}
          {...inputProps}
        />
        {isPassword ? (
          <button
            type="button"
            aria-label={isRevealed ? '입력 내용 숨기기' : '입력 내용 보기'}
            aria-pressed={isRevealed}
            aria-controls={id}
            onClick={() => setIsRevealed((value) => !value)}
            className="absolute inset-y-0 right-0 flex w-10 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            {isRevealed ? (
              <EyeOff aria-hidden strokeWidth={1.5} className="size-[18px]" />
            ) : (
              <Eye aria-hidden strokeWidth={1.5} className="size-[18px]" />
            )}
          </button>
        ) : null}
      </div>

      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
